'use client';

import { useMemo } from 'react';
import { useActionHook } from '@/lib/query/use-action-hook';
import { ACTION_KIND, type ActionResult } from '@/types/async-protocol';

export const SIGNED_URL_FETCH_AS = {
  Text: 'text',
  Blob: 'blob',
} as const;
export type SignedUrlFetchAs = typeof SIGNED_URL_FETCH_AS[keyof typeof SIGNED_URL_FETCH_AS];

export interface SignedUrlFetchVars {
  url: string;
  as: SignedUrlFetchAs;
}

export type SignedUrlFetchResult = { text: string } | { blob: Blob };

async function fetchSignedUrl(
  { url, as }: SignedUrlFetchVars,
  ctx?: { signal: AbortSignal },
): Promise<SignedUrlFetchResult> {
  const response = await fetch(url, { signal: ctx?.signal });
  if (!response.ok) throw new Error(`Signed URL fetch failed: ${response.status}`);
  if (as === SIGNED_URL_FETCH_AS.Text) return { text: await response.text() };
  return { blob: await response.blob() };
}

export function useSignedUrlFetch(): ActionResult<SignedUrlFetchVars, SignedUrlFetchResult> {
  return useActionHook<SignedUrlFetchVars, SignedUrlFetchResult>(
    useMemo(
      () => ({
        mutationFn: fetchSignedUrl,
        abortable: true,
        kind: ACTION_KIND.Query,
      }),
      [],
    ),
  );
}
